import { useEffect, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ContractStatus,
  DEPLOYMENT_KIND_LABELS,
  DeploymentKind,
  type StoreSummaryDto,
} from '@bhops/shared';
import { DeploymentStatusCell } from '@/lib/deployment-status-label';
import { storesApi } from '@/api/stores';
import { customersApi } from '@/api/customers';
import { contractsApi } from '@/api/contracts';
import { deploymentsApi } from '@/api/deployments';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { StoreEditModal } from './StoreEditModal';
import { MenuOfferingTab } from './MenuOfferingTab';
import { StoreSolutionsSection } from './StoreSolutionsSection';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';

// docs/work_log/pdf-feedback-followups-2026-05-24 — StoreDetailPage.
//
// 매장 단일 상세 페이지. PDF §Phase C 의 "영업 4개 탭 cross-link" 마무리
// — 매장 → 고객사 / 계약 / 배치 / 인프라 발주 reverse-lookup 모두 한
// 화면에. 기존엔 StoresPage list 만 있고 detail 없어 매장 선택 시 갈
// 곳이 없었음.
//
// 섹션 구조 (CustomerDetailPage 와 유사):
//   1. Overview — name / 지점 / 주소 / 활성 / 소유 법인
//   2. 고객사 — 매장의 customer 직접 link (이미 list 에 있던 정보를 detail 에서도)
//   3. 계약 — 이 매장이 연결된 contract 목록 (deployment 의 customer 기반)
//   4. 배치 — 이 매장에 있는 active 로봇 (deploymentsApi.list({ storeId }))
//   5. 인프라 발주 — store 별 install/grill 발주 (infrastructureOrdersApi 의 customerId)
//
// 데이터 가져오기 전략:
//   - store: 1 query (storesApi.get)
//   - customer: store.customerId 가 있을 때만 1 query
//   - 배치: deploymentsApi.list({ storeId, activeOnly: true })
//   - 계약 / 인프라: customerId 가 있어야 fetch — 없으면 섹션 자체 skip

type Tab = 'overview' | 'menu';
const STORE_TABS: Tab[] = ['overview', 'menu'];

export function StoreDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [editing, setEditing] = useState<StoreSummaryDto | null>(null);
  // round3 #8a — 메뉴 매트릭스 편집 권한(백엔드 RBAC 와 동일: VIEWER 제외).
  const userRole = useAuthStore((s) => s.user?.role);
  const canEditMenu = userRole ? canEditAny(userRole) : false;
  // docs/work_log/pdf-feedback-followups-2026-05-24 §menu-offerings-tab —
  // 기존 별도 페이지 (/stores/:id/menu-offerings → StoreMenuOfferingsPage)
  // 를 detail 의 tab 으로 통합. 운영자가 detail 한 곳에서 모든 매장
  // 정보 + 메뉴 구성 모두 관리 가능. 기존 deep link 는 redirect 로
  // 유지 (router 에서 /stores/:id/menu-offerings → /business/stores/:id?tab=menu).
  //
  // Tab state in URL — refresh / deep-link safe.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') ?? 'overview';
  const tab: Tab = STORE_TABS.includes(rawTab as Tab) ? (rawTab as Tab) : 'overview';
  // Strip invalid `?tab=` values so keyboard focus + tabpanel rendering stay
  // consistent. Without this, an unknown tab makes every TabButton tabIndex=-1
  // and renders no panel.
  useEffect(() => {
    if (rawTab !== tab) {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      setSearchParams(params, { replace: true });
    }
  }, [rawTab, tab]);
  function setTab(next: Tab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }
  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, currentTab: Tab) {
    const idx = STORE_TABS.indexOf(currentTab);
    if (idx < 0) return;
    let next: Tab | null = null;
    if (e.key === 'ArrowRight') next = STORE_TABS[(idx + 1) % STORE_TABS.length];
    else if (e.key === 'ArrowLeft')
      next = STORE_TABS[(idx - 1 + STORE_TABS.length) % STORE_TABS.length];
    else if (e.key === 'Home') next = STORE_TABS[0];
    else if (e.key === 'End') next = STORE_TABS[STORE_TABS.length - 1];
    if (next) {
      e.preventDefault();
      const target = next;
      setTab(target);
      requestAnimationFrame(() => {
        document.getElementById(`tab-${target}`)?.focus();
      });
    }
  }

  const storeQ = useQuery({
    queryKey: ['store', id],
    queryFn: () => storesApi.get(id),
    enabled: !!id,
  });
  const store = storeQ.data;

  const customerQ = useQuery({
    queryKey: ['customer', store?.customerId],
    queryFn: () => customersApi.get(store!.customerId!),
    enabled: !!store?.customerId,
  });

  if (storeQ.isLoading) return <div className="kuun-caption">불러오는 중…</div>;
  if (storeQ.isError || !store) {
    return (
      <div style={{ color: 'var(--fg-danger)' }}>
        {extractApiError(storeQ.error) || '매장을 불러올 수 없습니다.'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Link to="/business/stores" className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          ← 매장 목록
        </Link>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            marginTop: 6,
            flexWrap: 'wrap',
          }}
        >
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            {store.name}
            {store.branchName && (
              <span style={{ color: 'var(--fg-muted)', marginLeft: 8, fontSize: 16 }}>
                · {store.branchName}
              </span>
            )}
          </h1>
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 'var(--r-pill)',
              background: store.active ? 'var(--bg-success-subtle)' : 'var(--bg-sunken)',
              color: store.active ? 'var(--fg-on-success-tint)' : 'var(--fg-muted)',
              fontWeight: 600,
            }}
          >
            {store.active ? '활성' : '비활성'}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Button type="button" variant="secondary" onClick={() => setEditing(store)}>
              편집
            </Button>
          </div>
        </div>
      </div>

      {/* Tab strip — Overview (default) / 메뉴 구성. Tab state synced to
          ?tab= so refresh + deep links preserve selection. */}
      <div
        role="tablist"
        aria-label="매장 상세 탭"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--border-subtle)',
          marginBottom: 8,
        }}
      >
        <TabButton
          tabKey="overview"
          active={tab === 'overview'}
          onClick={() => setTab('overview')}
          onKeyDown={(e) => handleTabKeyDown(e, 'overview')}
        >
          개요
        </TabButton>
        <TabButton
          tabKey="menu"
          active={tab === 'menu'}
          onClick={() => setTab('menu')}
          onKeyDown={(e) => handleTabKeyDown(e, 'menu')}
        >
          메뉴 구성
        </TabButton>
      </div>

      {tab === 'overview' && (
        <div
          role="tabpanel"
          id="panel-overview"
          aria-labelledby="tab-overview"
          style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          <OverviewSection store={store} />
          <CustomerSection
            customerId={store.customerId ?? null}
            customer={customerQ.data ?? null}
            loading={customerQ.isLoading}
          />
          <DeploymentsSection storeId={store.id} />
          <StoreSolutionsSection storeId={store.id} />
          {store.customerId && <ContractsSection customerId={store.customerId} />}
          {store.customerId && <InfrastructureSection customerId={store.customerId} />}
        </div>
      )}
      {tab === 'menu' && (
        <div role="tabpanel" id="panel-menu" aria-labelledby="tab-menu">
          <MenuOfferingTab storeId={store.id} canEdit={canEditMenu} />
        </div>
      )}

      <StoreEditModal
        open={!!editing}
        onClose={() => {
          setEditing(null);
          // 편집 후 navigate 안 함 — useQuery invalidate 가 fresh data 가져옴.
        }}
        store={editing ?? store}
      />
    </div>
  );
}

function TabButton({
  tabKey,
  active,
  onClick,
  onKeyDown,
  children,
}: {
  tabKey: Tab;
  active: boolean;
  onClick: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={`tab-${tabKey}`}
      aria-selected={active}
      aria-controls={`panel-${tabKey}`}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        padding: '10px 14px',
        border: 'none',
        background: 'transparent',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--fg-brand)' : 'var(--fg-muted)',
        borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function OverviewSection({ store }: { store: StoreSummaryDto }) {
  return (
    <section>
      <SectionHeader title="기본 정보" />
      <div style={card}>
        <div style={kvGrid}>
          <KV label="매장명" value={store.name} />
          {store.branchName && <KV label="지점" value={store.branchName} />}
          <KV
            label="주소"
            value={
              [store.addressLine1, store.city, store.region].filter(Boolean).join(' · ') || '–'
            }
          />
          <KV label="국가" value={store.country} mono />
          <KV
            label="소유 법인"
            value={store.ownerEntity === 'BEYOND_HONEYCOMB' ? '비욘드허니컴' : '허니컴로보틱스'}
          />
          {/* 추가수정 2차(26-07-27) §2 — UTC 자르기 대신 KST. 한국 새벽 0~9시에
              만든 매장이 전날 생성으로 보이던 자리다. */}
          <KV label="생성일" value={kstYmd(store.createdAt) ?? '-'} mono />
        </div>
      </div>
    </section>
  );
}

function CustomerSection({
  customerId,
  customer,
  loading,
}: {
  customerId: string | null;
  customer: { id: string; displayName: string; legalName: string } | null;
  loading: boolean;
}) {
  if (!customerId) {
    return (
      <section>
        <SectionHeader title="고객사" />
        <div style={card}>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            연결된 고객사가 없습니다.
          </div>
        </div>
      </section>
    );
  }
  return (
    <section>
      <SectionHeader title="고객사" />
      <div style={card}>
        {loading ? (
          <div className="kuun-caption">불러오는 중…</div>
        ) : customer ? (
          <Link
            to={`/customers/${customer.id}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-input)',
              color: 'var(--fg-brand)',
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {customer.displayName}
            <span style={{ color: 'var(--fg-muted)', fontWeight: 400, fontSize: 11 }}>
              · {customer.legalName}
            </span>
            <span style={{ marginLeft: 4 }}>→</span>
          </Link>
        ) : (
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            고객사 정보를 불러올 수 없습니다.
          </div>
        )}
      </div>
    </section>
  );
}

function DeploymentsSection({ storeId }: { storeId: string }) {
  const q = useQuery({
    queryKey: ['deployments', { storeId, activeOnly: true }],
    queryFn: () => deploymentsApi.list({ storeId, activeOnly: true, pageSize: 100 }),
  });
  const items = q.data?.items ?? [];
  return (
    <section>
      <SectionHeader title="현재 배치 로봇" count={items.length} />
      <div style={card}>
        {q.isLoading ? (
          <div className="kuun-caption">불러오는 중…</div>
        ) : items.length === 0 ? (
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            현재 이 매장에 배치된 로봇이 없습니다.
          </div>
        ) : (
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>로봇 시리얼</th>
                <th style={th}>유형</th>
                <th style={th}>배치일</th>
                <th style={th}>상태</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                // PDF §Phase B 페이지 2 §5 — robot serial 표시 패턴 동일.
                const serial =
                  d.robot?.serialShort ?? d.robot?.serialLong ?? `${d.robotId.slice(0, 8)}…`;
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                      <Link to={`/robots/${d.robotId}`} style={linkStyle}>
                        {serial}
                      </Link>
                    </td>
                    {/* 2026-09-23 — 영문 코드(EVENT·CERTIFICATION…) 대신 정본 이름표. */}
                    <td style={td}>{DEPLOYMENT_KIND_LABELS[d.kind as DeploymentKind] ?? d.kind}</td>
                    <td style={tdMono}>{kstYmd(d.deployedAt) ?? '-'}</td>
                    <td style={td}>
                      <DeploymentStatusCell kind={d.kind} returnedAt={d.returnedAt} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function ContractsSection({ customerId }: { customerId: string }) {
  const q = useQuery({
    queryKey: ['contracts', { customerId, status: ContractStatus.ACTIVE }],
    queryFn: () => contractsApi.list({ customerId, status: ContractStatus.ACTIVE, pageSize: 20 }),
  });
  const items = q.data?.items ?? [];
  if (q.isLoading || items.length === 0) return null;
  return (
    <section>
      <SectionHeader title="활성 계약 (고객사 기준)" count={items.length} />
      <div style={card}>
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>계약</th>
              <th style={th}>기간</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{c.humanId}</td>
                <td style={tdMono}>
                  {kstYmd(c.startDate) ?? '-'} ~ {kstYmd(c.endDate) ?? '-'}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {/* FB §3 — 계약 상세 미사용 → 고객사 상세로. 이 섹션은
                      customerId 로 스코프되므로 그 값으로 직접 이동. */}
                  <Link to={`/customers/${customerId}`} style={linkStyle}>
                    열기 →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InfrastructureSection({ customerId }: { customerId: string }) {
  const q = useQuery({
    queryKey: ['infrastructure-orders', { customerId }],
    queryFn: () => infrastructureOrdersApi.list({ customerId }),
  });
  const items = q.data ?? [];
  if (q.isLoading || items.length === 0) return null;
  return (
    <section>
      <SectionHeader title="인프라 발주 (고객사 기준)" count={items.length} />
      <div style={card}>
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>계약</th>
              <th style={th}>가스</th>
              <th style={th}>설치 예정</th>
              <th style={th}>완료</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 10).map((o) => (
              <tr key={o.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                  {/* FB §3 — 계약 상세 미사용 → 고객사 상세로. 라벨은 계약
                      번호를 유지하되 이동 대상은 이 섹션의 고객사. */}
                  <Link to={`/customers/${customerId}`} style={linkStyle}>
                    {o.contract?.humanId ?? o.contractId.slice(0, 8)}
                  </Link>
                </td>
                <td style={td}>{o.gasType ?? '–'}</td>
                <td style={tdMono}>{kstYmd(o.installScheduledAt) ?? '-'}</td>
                <td style={tdMono}>{kstYmd(o.installCompletedAt) ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
      <h3 className="kuun-h3" style={{ margin: 0 }}>
        {title}
      </h3>
      {typeof count === 'number' && (
        <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          {count}건
        </span>
      )}
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--fg-default)',
          fontFamily: mono ? 'var(--font-mono)' : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
  overflow: 'auto',
};
const tbl: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const kvGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
};
const linkStyle: CSSProperties = {
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};
